import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Mail, Phone, Calendar, Car, User, MessageSquare } from "lucide-react";
import { AdminVehicles } from "@/components/AdminVehicles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "owrs_admin_pw";

export default function AdminPage() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState({ bookings: [], contacts: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("bookings");

  const fetchData = async (password) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { "X-Admin-Password": password },
      });
      setData(res.data);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, password);
    } catch (err) {
      setError(err?.response?.status === 401 ? "Incorrect password." : "Failed to load submissions.");
      setAuthed(false);
      sessionStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pw) fetchData(pw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (pw.trim()) fetchData(pw.trim());
  };

  const logout = () => {
    setAuthed(false);
    setPw("");
    setData({ bookings: [], contacts: [] });
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  if (!authed) {
    return (
      <section className="section min-h-[60vh] flex items-center">
        <div className="container-owrs max-w-md">
          <div className="overline mb-4">Restricted Area</div>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tighter mb-6">
            Admin <span className="text-[var(--owrs-accent)]">access</span>
          </h1>
          <form onSubmit={handleLogin} className="surface p-6 sm:p-8">
            <label className="owrs-label" htmlFor="admin-pw">Password</label>
            <div className="relative">
              <input
                id="admin-pw"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="admin-password-input"
                autoFocus
                className="owrs-input pr-10"
                placeholder="Enter admin password"
              />
              <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--owrs-text-dim)]" />
            </div>
            {error && (
              <div className="mt-3 text-sm text-red-400" data-testid="admin-error">{error}</div>
            )}
            <button type="submit" data-testid="admin-login-submit"
              disabled={loading}
              className="btn-primary mt-5 w-full disabled:opacity-60">
              {loading ? "Verifying…" : "Sign in"}
            </button>
          </form>
        </div>
      </section>
    );
  }

  const rows = tab === "bookings" ? data.bookings : data.contacts;

  return (
    <section className="section">
      <div className="container-owrs">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="overline mb-3">Admin</div>
            <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tighter">
              Submissions
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchData(pw)} data-testid="admin-refresh"
              className="btn-secondary !py-2 !px-4 text-sm">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout"
              className="btn-secondary !py-2 !px-4 text-sm">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        <div className="flex gap-1 mb-6 border-b border-[var(--owrs-border)]">
          {[
            { id: "bookings", label: `Bookings (${data.bookings.length})` },
            { id: "contacts", label: `Contacts (${data.contacts.length})` },
            { id: "vehicles", label: "Vehicles For Sale" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-3 text-sm tracking-wide border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-[var(--owrs-accent)] text-[var(--owrs-text)]"
                  : "border-transparent text-[var(--owrs-text-muted)] hover:text-[var(--owrs-text)]"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "vehicles" ? (
          <AdminVehicles pw={pw} />
        ) : rows.length === 0 ? (
          <div className="surface p-10 text-center text-[var(--owrs-text-muted)]">
            No {tab} yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid={`admin-list-${tab}`}>
            {rows.map((r) => (
              <div key={r.id} className="surface p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="text-xs text-[var(--owrs-text-dim)] tracking-wider uppercase mb-1">
                      {fmt(r.created_at)}
                    </div>
                    <div className="font-display text-lg sm:text-xl">
                      {r.name || "—"}
                      {r.registration && (
                        <span className="ml-3 inline-block px-2 py-0.5 bg-[var(--owrs-accent)] text-[var(--owrs-accent-ink)] text-xs font-mono tracking-wider">
                          {r.registration.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:items-end gap-1 text-sm">
                    {r.phone && (
                      <a href={`tel:${r.phone}`} className="hover:text-[var(--owrs-accent)]">
                        <Phone className="inline h-3.5 w-3.5 mr-1" />{r.phone}
                      </a>
                    )}
                    {r.email && (
                      <a href={`mailto:${r.email}`} className="hover:text-[var(--owrs-accent)] break-all">
                        <Mail className="inline h-3.5 w-3.5 mr-1" />{r.email}
                      </a>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  {r.requested_date && (
                    <Field icon={<Calendar className="h-4 w-4" />} label="Requested date"
                      value={new Date(r.requested_date).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })} />
                  )}
                  {r.vehicle && <Field icon={<Car className="h-4 w-4" />} label="Vehicle" value={r.vehicle} />}
                  {r.work_required && <Field icon={<MessageSquare className="h-4 w-4" />} label="Work required" value={r.work_required} full />}
                  {r.referral_source && <Field icon={<User className="h-4 w-4" />} label="How they found us" value={r.referral_source} />}
                  {r.message && <Field icon={<MessageSquare className="h-4 w-4" />} label="Message" value={r.message} full />}
                  {r.notes && <Field icon={<MessageSquare className="h-4 w-4" />} label="Notes" value={r.notes} full />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Field({ icon, label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="flex items-center gap-2 overline mb-1 !text-[var(--owrs-text-dim)]">
        {icon}<span>{label}</span>
      </div>
      <div className="text-[var(--owrs-text)] whitespace-pre-wrap leading-relaxed">{value}</div>
    </div>
  );
}
